import { PagedSoftwareSettingsMetadataList, SettingsTypeEnumSchema, SoftwareEnumSchema, SoftwareSettingsMetadata } from "@domain/schemas/BI";
import axios from "axios";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { ISoftwareSettingsRepository } from "@domain/repositories";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import { deleteProjectStorageFile } from "./ProjectStorageFileDeletion";
@injectable()
export class SoftwareSettingsRepository implements ISoftwareSettingsRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async createAsync(
    settingsFile: File,
    softwareVersion: string,
    settingsType: SettingsTypeEnumSchema,
    onProgress: (progress: number) => void,
    projectId: string,
    software: SoftwareEnumSchema = "Spectronaut" as SoftwareEnumSchema,
    rootPath?: string,
    folder?: string,
  ): Promise<SoftwareSettingsMetadata> {
    const formData = new FormData();
    formData.append("file", settingsFile);

    let url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareSettingsCreate}`
      .replace("{projectId}", () => encodeURIComponent(projectId))
      .replace("{software}", () => encodeURIComponent(software))
      .replace("{version}", () => encodeURIComponent(softwareVersion))
      .replace("{type}", () => settingsType);

    const query = new URLSearchParams();
    if (rootPath) {
      query.set("rootPath", rootPath);
    }
    if (folder) {
      query.set("folder", folder);
    }
    const queryString = query.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return this._requestHandlerService.handleRequestAsync<SoftwareSettingsMetadata>(() =>
      axios.post<SoftwareSettingsMetadata>(url, formData, {
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) {
            return;
          }

          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        },
      }),
    );
  }

  public async checkFileExistsAsync(projectId: string, fileName: string, rootPath?: string, folder?: string): Promise<number | null> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareSettingsCheckFile}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const params: Record<string, string> = { fileName };
    if (rootPath) {
      params.rootPath = rootPath;
    }
    if (folder) {
      params.folder = folder;
    }
    return this._requestHandlerService.handleRequestAsync<number | null>(() => axios.get<number | null>(url, { params }));
  }

  async GetListAsync(projectId: string, type: SettingsTypeEnumSchema, software?: SoftwareEnumSchema, folder?: string | null, sourcePath?: string | null): Promise<PagedSoftwareSettingsMetadataList> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareSettingsListFiles}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const params: Record<string, string> = { type };
    if (software) {
      params.software = software;
    }
    if (folder) {
      params.folder = folder;
    }
    if (sourcePath) {
      params.sourcePath = sourcePath;
    }
    return this._requestHandlerService.handleRequestAsync<PagedSoftwareSettingsMetadataList>(() => axios.get<PagedSoftwareSettingsMetadataList>(url, { params }));
  }

  async deleteAsync(projectId: string, fileName: string, settingsType: SettingsTypeEnumSchema, folder?: string | null, rootPath?: string | null): Promise<boolean> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareSettingsDelete}`.replace("{projectId}", () => encodeURIComponent(projectId));
    return deleteProjectStorageFile(this._requestHandlerService, url, {
      fileName,
      type: settingsType,
      folder: folder ?? undefined,
      rootPath: rootPath ?? undefined,
    });
  }
}
